import { Grid, Link as MaterialLink } from '@mui/material';
import axios from 'axios';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { PolicyPriceChangeResponse } from '../../../types/policy';
import { RenewalUpdateRequest } from '../../../types/renewals';
import {
	StyledContainer,
	StyledSectionContainer,
	StyledTitle,
} from '../../common';
import PricePreviewChangesTables from '../PricePreviewChangesTables';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
	errorData: ErrorData;
}

/**
 * The price preview page for an renewal.
 */
const PricePreview = (props: Props) => {
	const [priceData, setPriceData] = useState<PolicyPriceChangeResponse>(null);

	const { authData, renewalData, errorData } = props;

	const { policy, productConfig, renewalUpdateObj, setLoading } = renewalData;

	const authToken = authData.auth.authorizationToken;

	useEffect(() => {
		const source = axios.CancelToken.source();
		const load = async () => {
			if (renewalUpdateObj.state !== 'application') {
				setPriceData(renewalUpdateObj.price);
				return;
			}

			setLoading(true);

			const policyUpdateRequest: RenewalUpdateRequest = {
				...renewalUpdateObj,
			};

			if (!_.isEmpty(renewalUpdateObj.addExposures)) {
				policyUpdateRequest.addExposures = renewalUpdateObj.addExposures;
			}
			if (!_.isEmpty(renewalUpdateObj.updateExposures)) {
				policyUpdateRequest.updateExposures = renewalUpdateObj.updateExposures;
			}
			if (!_.isEmpty(renewalUpdateObj.addFieldGroups)) {
				policyUpdateRequest.addFieldGroups = renewalUpdateObj.addFieldGroups;
			}
			if (!_.isEmpty(renewalUpdateObj.updateFieldGroups)) {
				policyUpdateRequest.updateFieldGroups =
					renewalUpdateObj.updateFieldGroups;
			}
			if (!_.isEmpty(renewalUpdateObj.endExposures)) {
				policyUpdateRequest.endExposures = renewalUpdateObj.endExposures;
			}
			if (!_.isEmpty(renewalUpdateObj.fieldValues)) {
				policyUpdateRequest.fieldValues = renewalUpdateObj.fieldValues;
			}
			if (!_.isEmpty(renewalUpdateObj.removeFieldGroups)) {
				policyUpdateRequest.removeFieldGroups =
					renewalUpdateObj.removeFieldGroups;
			}

			try {
				const priceResponse = await RenewalsApi.previewRenewalPrice(
					authToken,
					renewalUpdateObj.locator,
				);

				if (priceResponse) {
					setPriceData(priceResponse.data);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					setLoading(false);
					return;
				} else {
					errorData.handleErrorResponse(error, 'Failed to fetch pricing');
					setLoading(false);
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
				}
			}

			setLoading(false);
		};

		load();

		return () => {
			source.cancel();
		};
	}, []);

	const renderPriceTables = () => {
		if (!priceData) {
			return (
				<div style={{ textAlign: 'center', width: '100%' }}>
					No pricing breakdown available
				</div>
			);
		}
		return (
			<PricePreviewChangesTables
				productConfig={productConfig}
				priceData={priceData}
				policy={policy}
				automatedUnderwritingResult={
					renewalUpdateObj && renewalUpdateObj.automatedUnderwritingResult
				}
			/>
		);
	};

	return (
		<>
			<Grid container>
				<Grid item xs={12} alignSelf='flex-end' sx={{ textAlign: 'right' }}>
					{authData.hasPermission('auxData.fetch') && (
						<MaterialLink component={Link} to='../auxData'>
							Aux Data
						</MaterialLink>
					)}
				</Grid>
			</Grid>
			<StyledContainer>
				<StyledTitle>Pricing Breakdown</StyledTitle>
			</StyledContainer>
			<StyledSectionContainer>{renderPriceTables()}</StyledSectionContainer>
		</>
	);
};

export default withRenewalContext<Props>(PricePreview);
